import { cn } from "@/utils/cn";
import type { DetailSource, PlaygroundPaper } from "../types";
import { detailSourceFor } from "../utils/detailSource";
import { DOT, META, TEXT_ACTION } from "./styles";

/** DOM id for a document's row, used by the result-set minimap to
 * scroll-sync dots with the visible list. */
export const docAnchorId = (docId: string) => `pg-doc-${docId}`;

const sourceLabel = (source?: string): string => {
  switch (source?.toLowerCase()) {
    case "pmc":
      return "PMC";
    case "biorxiv":
      return "bioRxiv";
    case "medrxiv":
      return "medRxiv";
    case "arxiv":
      return "arXiv";
    default:
      return source || "doc";
  }
};

const ROW = "group scroll-mt-24 border-b border-zinc-200/70 py-4 first:pt-0 -mx-3 px-3 transition-colors";
const ROW_ACTIVE = "bg-zinc-900/[0.03]";

const Title = ({
  children,
  onOpen,
}: {
  children: React.ReactNode;
  onOpen?: () => void;
}) =>
  onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      className="block text-left text-base leading-7 text-zinc-900 transition-colors hover:text-zinc-600"
    >
      {children}
    </button>
  ) : (
    <p className="text-base leading-7 text-zinc-900">{children}</p>
  );

const PaperCard = ({
  paper,
  detailSource,
  active,
  onOpen,
  onGrep,
}: {
  paper: PlaygroundPaper;
  detailSource: DetailSource | null;
  active?: boolean;
  onOpen: (source: DetailSource) => void;
  onGrep: () => void;
}) => (
  <li id={docAnchorId(paper.document_id)} className={cn(ROW, active && ROW_ACTIVE)}>
    <Title onOpen={detailSource ? () => onOpen(detailSource) : undefined}>
      {paper.title || "Untitled"}
    </Title>

    <p className={`${META} mt-0.5 flex flex-wrap items-baseline gap-x-2`}>
      {paper.authors && <span className="max-w-[34ch] truncate">{paper.authors}</span>}
      {paper.authors && <span aria-hidden className={DOT}>·</span>}
      <span>{sourceLabel(paper.source)}</span>
      {paper.pub_year ? (
        <>
          <span aria-hidden className={DOT}>·</span>
          <span>{paper.pub_year}</span>
        </>
      ) : null}
      <span aria-hidden className={DOT}>·</span>
      <span className="font-mono text-2xs text-zinc-400">{paper.document_id}</span>
    </p>

    {paper.abstract_snippet && (
      <p className="mt-2 line-clamp-3 max-w-[60ch] text-sm leading-6 text-zinc-600">
        {paper.abstract_snippet}
      </p>
    )}

    {detailSource === "papers" && (
      <p className="mt-1.5 flex gap-4 text-sm">
        <button type="button" onClick={() => onOpen(detailSource)} className={`${TEXT_ACTION} !text-sm`}>
          Read
        </button>
        <button type="button" onClick={onGrep} className={`${TEXT_ACTION} !text-sm`}>
          Grep this paper
        </button>
      </p>
    )}
  </li>
);

/** A UniProt hit — click-through opens the protein profile panel instead of
 * the block viewer (proteins have structured data, not content blocks). */
const ProteinCard = ({
  paper,
  active,
  onOpen,
}: {
  paper: PlaygroundPaper;
  active?: boolean;
  onOpen: () => void;
}) => (
  <li id={docAnchorId(paper.document_id)} className={cn(ROW, active && ROW_ACTIVE)}>
    <Title onOpen={onOpen}>
      {paper.gene_name ? `${paper.gene_name} — ` : ""}
      {paper.protein_name ?? paper.title ?? "Unnamed protein"}
    </Title>
    <p className={`${META} mt-0.5 flex flex-wrap items-baseline gap-x-2`}>
      <span>UniProt</span>
      {paper.organism && (
        <>
          <span aria-hidden className={DOT}>·</span>
          <span>{paper.organism}</span>
        </>
      )}
      {paper.sequence_length != null && (
        <>
          <span aria-hidden className={DOT}>·</span>
          <span>{paper.sequence_length} aa</span>
        </>
      )}
      <span aria-hidden className={DOT}>·</span>
      <span className="font-mono text-2xs text-zinc-400">{paper.accession ?? paper.document_id}</span>
    </p>
    <p className="mt-1.5 flex gap-4 text-sm">
      <button type="button" onClick={onOpen} className={`${TEXT_ACTION} !text-sm`}>
        Open profile
      </button>
      <span className="text-zinc-400">PDB structures + ChEMBL drugs joined on the accession</span>
    </p>
  </li>
);

export const SearchResultsView = ({
  papers,
  resultsId,
  searchSource,
  activeId,
  onOpenPaper,
  onGrepPaper,
  onOpenProtein,
}: {
  papers: PlaygroundPaper[];
  resultsId?: string | null;
  searchSource?: string;
  /** Scroll-synced document highlighted in the result-set minimap. */
  activeId?: string | null;
  onOpenPaper: (docId: string, source: DetailSource) => void;
  onGrepPaper: (docId: string) => void;
  onOpenProtein: (accession: string) => void;
}) => {
  if (papers.length === 0) {
    return (
      <p className="py-12 text-center text-base italic text-zinc-500">
        No documents matched this query. Try broader terms or a different source.
      </p>
    );
  }
  return (
    <div>
      <div className="mb-3 flex items-baseline gap-3 border-b border-zinc-200 pb-2 text-sm text-zinc-400">
        <span className="text-zinc-700">
          {papers.length} result{papers.length === 1 ? "" : "s"}
        </span>
        {resultsId && (
          <span
            className="font-mono text-2xs"
            title="Saved result set — map a question over it, or reuse it with filter/reduce in the CLI"
          >
            {resultsId}
          </span>
        )}
        <span className="ml-auto hidden italic sm:block">
          {searchSource === "proteins"
            ? "Click a protein to open its live profile"
            : "Click a title to read its blocks"}
        </span>
      </div>
      <ul>
        {papers.map((paper) =>
          searchSource === "proteins" ? (
            <ProteinCard
              key={paper.document_id}
              paper={paper}
              active={paper.document_id === activeId}
              onOpen={() => onOpenProtein(paper.accession ?? paper.document_id)}
            />
          ) : (
            <PaperCard
              key={paper.document_id}
              paper={paper}
              detailSource={detailSourceFor(paper.document_id, searchSource)}
              active={paper.document_id === activeId}
              onOpen={(source) => onOpenPaper(paper.document_id, source)}
              onGrep={() => onGrepPaper(paper.document_id)}
            />
          ),
        )}
      </ul>
    </div>
  );
};
